import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatCurrency, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Wallet, DoorOpen, DoorClosed, Calculator, Ticket, Clock, ArrowRight, Coins } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { motion } from "framer-motion";

export default function CashRegister() {
  const { company } = useAuth();
  const currency = company?.currency || "FCFA";
  const [session, setSession] = useState(null);
  const [history, setHistory] = useState([]);
  const [openingAmount, setOpeningAmount] = useState("");
  const [closingAmount, setClosingAmount] = useState("");
  const [amountDue, setAmountDue] = useState("");
  const [amountReceived, setAmountReceived] = useState("");
  const [change, setChange] = useState(null);

  const load = async () => {
    const [s, h] = await Promise.all([api.get("/cash/current"), api.get("/cash/history")]);
    setSession(s.data);
    setHistory(h.data);
  };
  useEffect(() => { load(); }, []);

  const openSession = async () => {
    try { await api.post("/cash/open", { opening_amount: Number(openingAmount) }); toast.success("Session ouverte"); setOpeningAmount(""); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const closeSession = async () => {
    try { await api.post("/cash/close", { closing_amount: Number(closingAmount), notes: "" }); toast.success("Session fermée"); setClosingAmount(""); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const computeChange = async () => {
    try { const { data } = await api.post("/cash/change", { amount_due: Number(amountDue), amount_received: Number(amountReceived) }); setChange(data.change); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold">Caisse</h1>
        <p className="text-sm text-slate-500">Vente au guichet (espèces + électronique) et gestion de la session</p>
      </div>

      {/* Ticket buttons (all payment methods including cash) */}
      <div className="grid md:grid-cols-2 gap-4">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <Link to="/app/book/reservation?context=cash" data-testid="cash-btn-reservation" className="group block">
            <div className="relative overflow-hidden rounded-2xl bg-orange-500 text-white p-6 min-h-[150px] flex flex-col justify-between hover:-translate-y-1 transition-transform shadow-md">
              <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full bg-white/10 blur-2xl"></div>
              <div className="absolute right-4 top-4"><Ticket className="w-8 h-8 opacity-70" /></div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-semibold opacity-80">Vente caisse</div>
                <div className="font-display text-2xl font-bold mt-1">Billet Réservation</div>
                <div className="mt-1 text-white/85 text-xs flex items-center gap-1"><Coins className="w-3 h-3" /> Espèces + électronique</div>
              </div>
              <div className="flex items-center gap-2 text-xs font-semibold">Émettre <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" /></div>
            </div>
          </Link>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
          <Link to="/app/book/realtime?context=cash" data-testid="cash-btn-realtime" className="group block">
            <div className="relative overflow-hidden rounded-2xl bg-slate-900 text-white p-6 min-h-[150px] flex flex-col justify-between hover:-translate-y-1 transition-transform border border-slate-200/10 shadow-md">
              <div className="absolute -left-6 -bottom-6 w-32 h-32 rounded-full bg-orange-500/25 blur-2xl"></div>
              <div className="absolute right-4 top-4"><Clock className="w-8 h-8 opacity-70" /></div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-semibold opacity-80">Vente caisse</div>
                <div className="font-display text-2xl font-bold mt-1">Billet Temps réel</div>
                <div className="mt-1 text-white/70 text-xs flex items-center gap-1"><Coins className="w-3 h-3" /> Espèces + électronique</div>
              </div>
              <div className="flex items-center gap-2 text-xs font-semibold">Émettre <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" /></div>
            </div>
          </Link>
        </motion.div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="font-display flex items-center gap-2"><Wallet className="w-5 h-5 text-orange-500" /> Session en cours</CardTitle></CardHeader>
          <CardContent>
            {session ? (
              <div className="space-y-3">
                <div className="text-sm">Ouverte par <b>{session.opened_by_name}</b></div>
                <div className="text-xs text-slate-500">{new Date(session.opened_at).toLocaleString("fr-FR")}</div>
                <div className="p-4 rounded-xl bg-orange-500 text-white">
                  <div className="text-xs uppercase tracking-widest opacity-80">Fond de caisse</div>
                  <div className="font-display font-bold text-2xl mt-1">{formatCurrency(session.opening_amount, currency)}</div>
                </div>
                <Label>Montant à la fermeture</Label>
                <Input type="number" value={closingAmount} onChange={(e) => setClosingAmount(e.target.value)} data-testid="closing-amount-input" />
                <Button onClick={closeSession} className="btn-pill bg-slate-900 hover:bg-slate-800 text-white w-full" data-testid="close-session-btn"><DoorClosed className="w-4 h-4 mr-1" /> Fermer la caisse</Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="text-sm text-slate-500">Aucune session ouverte</div>
                <Label>Fond de caisse initial</Label>
                <Input type="number" value={openingAmount} onChange={(e) => setOpeningAmount(e.target.value)} data-testid="opening-amount-input" placeholder="ex: 10000" />
                <Button onClick={openSession} className="btn-pill bg-orange-500 hover:bg-orange-600 w-full" data-testid="open-session-btn"><DoorOpen className="w-4 h-4 mr-1" /> Ouvrir la caisse</Button>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="font-display flex items-center gap-2"><Calculator className="w-5 h-5 text-orange-500" /> Calcul de monnaie</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div><Label>Prix du billet</Label><Input type="number" value={amountDue} onChange={(e) => setAmountDue(e.target.value)} data-testid="due-input" placeholder="5000" /></div>
            <div><Label>Montant reçu</Label><Input type="number" value={amountReceived} onChange={(e) => setAmountReceived(e.target.value)} data-testid="received-input" placeholder="10000" /></div>
            <Button onClick={computeChange} className="btn-pill bg-orange-500 hover:bg-orange-600 w-full" data-testid="compute-change-btn">Calculer</Button>
            {change !== null && (
              <div className="p-4 rounded-xl bg-emerald-500 text-white">
                <div className="text-xs uppercase tracking-widest opacity-80">Monnaie à rendre</div>
                <div className="font-display font-bold text-3xl mt-1" data-testid="change-result">{formatCurrency(change, currency)}</div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="font-display">Historique des sessions</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500 border-b border-slate-200 dark:border-slate-800">
              <tr><th className="text-left p-3">Ouverte</th><th className="text-left">Fermée</th><th className="text-left">Par</th><th className="text-right">Ouverture</th><th className="text-right">Ventes espèces</th><th className="text-right">Attendu</th><th className="text-right">Fermeture</th><th className="text-right p-3">Écart</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {history.map((s) => (
                <tr key={s.id}>
                  <td className="p-3 text-xs">{new Date(s.opened_at).toLocaleString("fr-FR")}</td>
                  <td className="text-xs">{s.closed_at ? new Date(s.closed_at).toLocaleString("fr-FR") : <span className="text-emerald-600">En cours</span>}</td>
                  <td>{s.opened_by_name}</td>
                  <td className="text-right">{formatCurrency(s.opening_amount, currency)}</td>
                  <td className="text-right">{formatCurrency(s.cash_sales || 0, currency)}</td>
                  <td className="text-right">{s.expected_amount ? formatCurrency(s.expected_amount, currency) : "—"}</td>
                  <td className="text-right">{s.closing_amount != null ? formatCurrency(s.closing_amount, currency) : "—"}</td>
                  <td className={`p-3 text-right font-semibold ${s.variance > 0 ? "text-emerald-600" : s.variance < 0 ? "text-red-600" : ""}`}>{s.variance != null ? formatCurrency(s.variance, currency) : "—"}</td>
                </tr>
              ))}
              {history.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-slate-500">Aucune session</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
